import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { IOrganizationState } from './organization.state';
import { Client } from '../../models/organization/client.model';
import { ClientBillingRun, ClientBillingRunListItem } from '../../models/organization/client-billing-run.model';
import { BillingItemType } from '../../models/organization/billing-item-type.model';
import { BillingSssBracket } from '../../models/organization/billing-sss-bracket.model';
import { Deployment } from '../../models/organization/deployment.model';
import { Department } from '../../models/organization/department.model';
import { Branch } from '../../models/organization/branch.model';
import { Position } from '../../models/organization/position.model';
import { WorkSchedule } from '../../models/organization/work-schedule.model';

export const withOrganizationStateMethods = signalStoreFeature(
  { state: type<IOrganizationState>() },
  withMethods((store) => ({
    setClients: (clients: Client[]) => {
      patchState(store, { clients, loading: false, error: null });
    },
    addClient: (client: Client) => {
      patchState(store, { clients: [client, ...store.clients()], loading: false, error: null });
    },
    updateClient: (client: Client) => {
      patchState(store, {
        clients: store.clients().map((c) =>
          c.clientGuid === client.clientGuid ? client : c
        ),
        loading: false,
        error: null,
      });
    },
    removeClient: (clientGuid: string) => {
      patchState(store, {
        clients: store.clients().filter((c) => c.clientGuid !== clientGuid),
        loading: false,
        error: null,
      });
    },
    setBillingRuns: (billingRuns: ClientBillingRunListItem[]) => {
      patchState(store, { billingRuns, loading: false, error: null });
    },
    setSelectedBillingRun: (selectedBillingRun: ClientBillingRun | null) => {
      patchState(store, { selectedBillingRun, loading: false, error: null });
    },
    removeBillingRun: (runGuid: string) => {
      patchState(store, {
        billingRuns: store.billingRuns().filter((r) => r.clientBillingRunGuid !== runGuid),
        selectedBillingRun:
          store.selectedBillingRun()?.clientBillingRunGuid === runGuid
            ? null
            : store.selectedBillingRun(),
        loading: false,
        error: null,
      });
    },
    setBillingItemTypes: (billingItemTypes: BillingItemType[]) => {
      patchState(store, { billingItemTypes, loading: false, error: null });
    },
    setBillingSssBrackets: (billingSssBrackets: BillingSssBracket[]) => {
      patchState(store, { billingSssBrackets, loading: false, error: null });
    },
    addBillingItemType: (item: BillingItemType) => {
      patchState(store, {
        billingItemTypes: [item, ...store.billingItemTypes()],
        loading: false,
        error: null,
      });
    },
    updateBillingItemType: (item: BillingItemType) => {
      patchState(store, {
        billingItemTypes: store.billingItemTypes().map((x) =>
          x.billingItemTypeGuid === item.billingItemTypeGuid ? item : x
        ),
        loading: false,
        error: null,
      });
    },
    removeBillingItemType: (itemGuid: string) => {
      patchState(store, {
        billingItemTypes: store.billingItemTypes().filter((x) => x.billingItemTypeGuid !== itemGuid),
        loading: false,
        error: null,
      });
    },
    setDeployments: (deployments: Deployment[]) => {
      patchState(store, { deployments, loading: false, error: null });
    },
    addDeployment: (deployment: Deployment) => {
      patchState(store, { deployments: [deployment, ...store.deployments()], loading: false, error: null });
    },
    updateDeployment: (deployment: Deployment) => {
      patchState(store, {
        deployments: store.deployments().map((d) =>
          d.deploymentGuid === deployment.deploymentGuid ? deployment : d
        ),
        loading: false,
        error: null,
      });
    },
    removeDeployment: (deploymentGuid: string) => {
      patchState(store, {
        deployments: store.deployments().filter((d) => d.deploymentGuid !== deploymentGuid),
        loading: false,
        error: null,
      });
    },
    setDepartments: (departments: Department[]) => {
      patchState(store, { departments, loading: false, error: null });
    },
    setBranches: (branches: Branch[]) => {
      patchState(store, { branches, loading: false, error: null });
    },
    setPositions: (positions: Position[]) => {
      patchState(store, { positions, filteredPositions: positions, loading: false, error: null });
    },
    setFilteredPositions: (departmentId: number) => {
      patchState(store, {
        filteredPositions: store.positions().filter((p) => p.departmentId === departmentId),
      });
    },
    clearFilteredPositions: () => {
      patchState(store, { filteredPositions: [] });
    },
    addDepartment: (department: Department) => {
      patchState(store, { departments: [department, ...store.departments()], loading: false, error: null });
    },
    updateDepartment: (department: Department) => {
      patchState(store, {
        departments: store.departments().map((d) =>
          d.departmentGuid === department.departmentGuid ? department : d
        ),
        loading: false,
        error: null,
      });
    },
    removeDepartment: (departmentGuid: string) => {
      patchState(store, {
        departments: store.departments().filter((d) => d.departmentGuid !== departmentGuid),
        loading: false,
        error: null,
      });
    },
    addBranch: (branch: Branch) => {
      patchState(store, { branches: [branch, ...store.branches()], loading: false, error: null });
    },
    updateBranch: (branch: Branch) => {
      patchState(store, {
        branches: store.branches().map((b) =>
          b.branchGuid === branch.branchGuid ? branch : b
        ),
        loading: false,
        error: null,
      });
    },
    removeBranch: (branchGuid: string) => {
      patchState(store, {
        branches: store.branches().filter((b) => b.branchGuid !== branchGuid),
        loading: false,
        error: null,
      });
    },
    addPosition: (position: Position) => {
      patchState(store, { positions: [position, ...store.positions()], loading: false, error: null });
    },
    updatePosition: (position: Position) => {
      patchState(store, {
        positions: store.positions().map((p) =>
          p.positionGuid === position.positionGuid ? position : p
        ),
        loading: false,
        error: null,
      });
    },
    removePosition: (positionGuid: string) => {
      patchState(store, {
        positions: store.positions().filter((p) => p.positionGuid !== positionGuid),
        loading: false,
        error: null,
      });
    },
    setWorkSchedules: (workSchedules: WorkSchedule[]) => {
      patchState(store, { workSchedules, loading: false, error: null });
    },
    addWorkSchedule: (workSchedule: WorkSchedule) => {
      patchState(store, { workSchedules: [workSchedule, ...store.workSchedules()], loading: false, error: null });
    },
    updateWorkSchedule: (workSchedule: WorkSchedule) => {
      patchState(store, {
        workSchedules: store.workSchedules().map((ws) =>
          ws.workScheduleGuid === workSchedule.workScheduleGuid ? workSchedule : ws
        ),
        loading: false,
        error: null,
      });
    },
    removeWorkSchedule: (workScheduleGuid: string) => {
      patchState(store, {
        workSchedules: store.workSchedules().filter((ws) => ws.workScheduleGuid !== workScheduleGuid),
        loading: false,
        error: null,
      });
    },
    setError: (error: string) => {
      patchState(store, { error, loading: false });
    },
  }))
);
